import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import InputGroup from 'reactstrap/lib/InputGroup';
import Label from 'reactstrap/lib/Label';
import { isEmpty } from 'lodash-es';

import { verify } from 'site-modules/shared/components/profile/firebase-auth';
import { formValidation } from 'site-modules/shared/components/form-validation/form-validation';
import { firebaseValidation } from 'site-modules/shared/components/profile/firebase-validation';

import { trackEmailOnlySignUpSuccess } from 'client/engagement-handlers/profile-engagement-handler/profile-engagement-handler';
import { Link } from 'site-modules/shared/components/link/link';
import { FormField } from 'site-modules/shared/components/form-field/form-field';
import { ProfileFormErrors } from 'site-modules/shared/components/profile/profile-form-errors/profile-form-errors';
import { SMART_SIGN_SCREEN, validators } from 'site-modules/shared/components/profile/smart-sign/helper';
import { ReCaptchaButtonAsync } from 'site-modules/shared/components/profile/recaptcha-button/recaptcha-button-async';

function SignEmailFormUI({
  validate,
  resetValidation,
  validateEmailAsync,
  validationErrors = {},
  fieldRef,
  fieldsOrder,
  fields,
  firebaseError = null,
  parseFirebaseError,
  isVerifyEmail,
  profileDataEmail = null,
  successCallback,
  labelText = 'Email *',
  ctaText = 'Continue',
  disclaimerPrefix = null,
  className = null,
  labelClassName = 'medium mb-0_25',
  buttonClassName = null,
  disclaimerClassName = 'mt-0_25 text-gray-darker size-10',
  errorsClassName = null,
  describedById = '',
  isAuthenticated = null,
  isSignInAction = false,
  isInlineButton = false,
  disableRecaptcha = false,
}) {
  const [isSpinnerActive, setSpinnerActive] = useState(false);

  function handleSubmitForm(event) {
    event.preventDefault();
  }

  async function handleSubmit(token) {
    const nextScreen = isVerifyEmail || isSignInAction ? SMART_SIGN_SCREEN.SIGN_IN : SMART_SIGN_SCREEN.SIGN_UP;

    setSpinnerActive(true);
    const email = fields.email.value();
    return verify({ email, token, disableRecaptcha })
      .then(async () => {
        const emailValidation = validate() && (await validateEmailAsync());
        if (emailValidation && emailValidation.isEmailValid) {
          const emailValue = fields.email.value();
          if (isAuthenticated && !profileDataEmail && nextScreen === SMART_SIGN_SCREEN.SIGN_UP) {
            trackEmailOnlySignUpSuccess();
          }

          successCallback({
            emailValue,
            nextScreen,
          });
        }
      })
      .catch(error => {
        parseFirebaseError(error);
      });
  }

  if ((!isEmpty(firebaseError) || !isEmpty(validationErrors)) && isSpinnerActive) {
    setSpinnerActive(false);
  }

  const isValidEmail = !('email' in validationErrors);

  const submitButton = (
    <ReCaptchaButtonAsync
      action={isSignInAction ? 'SIGN_IN' : 'SIGN_UP'}
      id="smart-sign-email-submit"
      isActionProgress={isSpinnerActive}
      handleAction={handleSubmit}
      color="blue-50"
      className={classnames('email-submit-button', buttonClassName, {
        'w-100': !isInlineButton,
        'spinner-active': isSpinnerActive,
      })}
      btnText={ctaText}
      btnActionText={ctaText}
    />
  );

  return (
    <div className={classnames('smart-sign-step-one', className)}>
      <form id="smart-sign-email-form" noValidate onSubmit={handleSubmitForm}>
        <Label for="sign-up-email" className={labelClassName}>
          {labelText}
        </Label>
        <InputGroup className="mb-0_5 flex-nowrap">
          <FormField
            id="sign-up-email"
            className="mb-0 w-100"
            labelClassName="medium mb-0_25"
            inputClassName="email-input inspectletIgnore"
            name="email"
            ref={field => fieldRef('email', field)}
            type="email"
            autoComplete="email"
            isValid={isValidEmail}
            onBlur={validate}
            onChange={resetValidation}
            data-tracking-id="provide_email"
            data-tracking-ignore-value
            data-no-refresh
            aria-required
            aria-invalid={!isValidEmail}
            aria-describedby={`sign-up-error profile-form-errors ${describedById}`}
            defaultValue={profileDataEmail}
          />
          {isInlineButton && submitButton}
        </InputGroup>
        {!isVerifyEmail && (
          <div className={disclaimerClassName}>
            {disclaimerPrefix}By clicking {ctaText}, I accept the terms of the{' '}
            <Link target="_blank" rel="noopener noreferrer" to="/about/visitor-agreement.html">
              <u>Edmunds Visitor Agreement</u>
            </Link>
            .{' '}
            <Link target="_blank" rel="noopener noreferrer" to="/about/privacy.html">
              <u>Our collection and use of your personal information</u>
            </Link>
            .
          </div>
        )}
        <ProfileFormErrors
          fieldsOrder={fieldsOrder}
          validationErrors={validationErrors}
          firebaseError={firebaseError}
          isAuthenticatedSaveModal
          className={classnames('xsmall', errorsClassName)}
        />
        {!isInlineButton && <div className="px-2_5 px-sm-3_5 mt-0_75">{submitButton}</div>}
      </form>
    </div>
  );
}

SignEmailFormUI.propTypes = {
  successCallback: PropTypes.func.isRequired,
  validateEmailAsync: PropTypes.func.isRequired,
  validate: PropTypes.func.isRequired,
  resetValidation: PropTypes.func.isRequired,
  validationErrors: PropTypes.objectOf(PropTypes.string),
  fieldRef: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool,
  fields: PropTypes.objectOf(
    PropTypes.shape({
      email: PropTypes.object,
    })
  ).isRequired,
  fieldsOrder: PropTypes.arrayOf(PropTypes.string).isRequired,
  firebaseError: PropTypes.shape({}),
  parseFirebaseError: PropTypes.func.isRequired,
  profileDataEmail: PropTypes.string,
  labelText: PropTypes.string,
  ctaText: PropTypes.string,
  disclaimerPrefix: PropTypes.string,
  className: PropTypes.string,
  labelClassName: PropTypes.string,
  buttonClassName: PropTypes.string,
  disclaimerClassName: PropTypes.string,
  errorsClassName: PropTypes.string,
  describedById: PropTypes.string,
  isVerifyEmail: PropTypes.bool.isRequired,
  isSignInAction: PropTypes.bool,
  isInlineButton: PropTypes.bool,
  disableRecaptcha: PropTypes.bool,
};

export const SignEmailForm = formValidation(firebaseValidation(SignEmailFormUI), validators);
